import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import classNames from 'classnames';

import { Chip, ChipSize, ChipVariant } from '@ui/uikit/components/common/Chip';

type Props = ComponentPropsWithoutRef<'li'> & {
  label: ReactNode;
  description: ReactNode;
  buttons: ReactNode;
  counter?: number;
  isLoading?: boolean;
  isDisabled?: boolean;
};

export const ListItem = ({
  label,
  description,
  buttons,
  counter,
  isLoading = false,
  isDisabled = false,
  ...props
}: Props) => {
  return (
    <li
      className={classNames('md:min-h-[4.5rem] border-b border-base-border-medium last:border-b-0')}
      {...props}
    >
      <div className="flex w-full items-center gap-3">
        <div className="flex w-full md:items-center py-5 gap-4 xl:gap-3 max-md:flex-col">
          <p
            className={classNames(
              'flex flex-col gap-1 xl:gap-0.5',
              isLoading && 'msq-skeleton-text h-4 w-[160px]',
            )}
          >
            <span
              className={classNames(
                'text-sm font-semibold xl:text-base text-base-text-primary',
                isDisabled && 'text-base-text-quaternary',
              )}
            >
              {label}
            </span>
            {description && (
              <span
                className={classNames(
                  'text-2xs leading-3 xl:text-xs font-medium text-base-text-tertiary',
                )}
              >
                {description}
              </span>
            )}
          </p>
          <div
            className={classNames(
              'flex gap-3 [&>*]:w-full md:[&>*]:w-[10rem] md:ml-auto max-sm:flex-col',
              isLoading && 'msq-skeleton-block h-8 w-[160px] max-md-msq:w-full',
            )}
          >
            {buttons}
          </div>
        </div>
        {typeof counter === 'number' && (
          <Chip
            variant={counter > 0 ? ChipVariant.GREEN : ChipVariant.DEFAULT}
            text={counter > 0 ? String(counter) : '+1'}
            size={ChipSize.LARGE}
            className="w-12"
          />
        )}
      </div>
    </li>
  );
};
